<template>
    <Card overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />

                <DropdownFilter v-model="conversion" :items="conversionItems" :prefix="`${$t('salesSummary.conversion')}: `" :disabled="loading" />

                <DropdownFilter v-model="period" :items="periodItems" :prefix="`${$t('period')}: `" :disabled="loading" />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :fetch-options-function="fetchGroups"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <DataTable
            :fields="fields"
            :items="items"
            :loading="loading"
            :hover="false"
            :overflow="false"
            :pagination="true"
            :per-page-values="perPageValues"
            :per-page.sync="perPage"
            :current-page.sync="page"
            :sort-by.sync="sortBy"
            :sort-direction.sync="sortDirection"
            :error="error"
            :header="$t('salesSummary.title')"
            show-export-button
            @update:error="error = null"
        >
            <template #cell(date)="{ item }">
                <template v-if="period === 'year'">{{ $format.date(item.date, 'yyyy') }}</template>
                <template v-else-if="period === 'quarter'">{{ $format.date(item.date, 'QQQ yyyy') }}</template>
                <template v-else-if="period === 'month'">{{ $format.date(item.date, 'LLLL yyyy') }}</template>
                <template v-else>{{ $format.date(item.date) }}</template>
            </template>
            <template #cell(views)="{ item }">{{ $format.number(item.views, 0) }}</template>
            <template #cell(clicks)="{ item }">{{ $format.number(item.clicks, 0) }}</template>
            <template #cell(ctr)="{ item }">{{ $format.percent(item.ctr) }}</template>
            <template #cell(transactions)="{ item }">{{ $format.number(item.transactions, 0) }}</template>
            <template #cell(cr)="{ item }">{{ $format.percent(item.cr) }}</template>
            <template #cell(quantity)="{ item }">{{ $format.number(item.quantity, 0) }}</template>
            <template #cell(amount)="{ item }">{{ $format.currency(item.amount) }}</template>
        </DataTable>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import salesReportMixin from '../salesReportMixin';
    import { fetchSources, fetchGroups } from '../utils';

    export default {
        name: 'SalesSummary',
        mixins: [salesReportMixin],
        components: { Card, Multiselect, DropdownFilter, DataTable },
        data() {
            return {
                requestSummary: this.$req('reportsSales/Summary'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),

                sources: [],
                groups: [],
                showProductGroupsResults: false,
                conversion: 24,
                period: 'day',
                conversionItems: ['all', 24],
                periodItems: ['day', 'month', 'quarter', 'year'],

                rows: [],
                error: null,

                sourcesLoading: false,
                groupsLoading: false,

                sortBy: 'date',
                sortDirection: 'DESC',
                perPage: 10,
                perPageValues: [10, 20, 50, null],
                page: 1,
            };
        },
        computed: {
            loading() {
                return this.sourcesLoading || this.groupsLoading || this.requestSummary.getLoading();
            },
            apiParams() {
                const { startDate, endDate } = this.timeRange || {};
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { startDate, endDate };
                if (this.sources?.length) requestParams.sources = this.sources.join(',');
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                if (this.conversion !== 'all') requestParams.conversionTime = Number(this.conversion);
                requestParams.groupBy = this.period;
                return { urlParams, requestParams };
            },
            fields() {
                return [
                    { key: 'date', label: this.$t('salesSummary.date'), sortable: true, class: 'text-left', exportable: true, exportFormatter: value => this.formatDate(value) },
                    {
                        key: 'views',
                        label: this.$t('salesSummary.views'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    {
                        key: 'clicks',
                        label: this.$t('salesSummary.clicks'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    { key: 'ctr', label: this.$t('salesSummary.ctr'), sortable: true, class: 'text-right', exportable: true, exportFormatter: value => this.$format.percent(value) },
                    {
                        key: 'transactions',
                        label: this.$t('salesSummary.transaction'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    { key: 'cr', label: this.$t('salesSummary.cr'), sortable: true, class: 'text-right', exportable: true, exportFormatter: value => this.$format.percent(value) },
                    {
                        key: 'quantity',
                        label: this.$t('salesSummary.quantity'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    {
                        key: 'amount',
                        label: this.$t('salesSummary.amount'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.currency(value),
                    },
                ];
            },
            items() {
                return this.rows || [];
            },
        },
        watch: {
            spaceId() {
                this.resetAndFetch();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.fetchData();
                },
            },
        },
        methods: {
            resetFilters() {
                this.conversion = 24;
                this.period = 'day';
                this.showProductGroupsResults = false;
                this.sortBy = 'date';
                this.sortDirection = 'DESC';
                this.perPage = 10;
                this.page = 1;
                this.resetAndFetch();
                this.fetchData(true);
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
            fetchGroups({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
            formatDate(val) {
                if (this.period === 'year') return this.$format.date(val, 'yyyy');
                if (this.period === 'quarter') return this.$format.date(val, 'QQQ yyyy');
                if (this.period === 'month') return this.$format.date(val, 'LLLL yyyy');
                return this.$format.date(val);
            },
            fetchData(force = false) {
                if (!this.spaceId || !this.timeRange || this.sourcesLoading || this.groupsLoading) return;

                const { urlParams, requestParams } = this.apiParams;
                const payload = {
                    urlParams,
                    requestParams: {
                        startDate: requestParams.startDate,
                        endDate: requestParams.endDate,
                        conversionTime: this.conversion === 'all' ? null : Number(this.conversion),
                        groupBy: this.period,
                        ...(requestParams.sources ? { sources: requestParams.sources } : {}),
                        ...(requestParams.groups ? { groups: requestParams.groups } : {}),
                    },
                };

                this.error = null;
                this.requestSummary
                    .get(payload, { force })
                    .then(resp => {
                        const rows = Array.isArray(resp) ? resp : [];
                        this.rows = rows.map(r => {
                            const views = Number(r.views ?? 0);
                            const clicks = Number(r.clicks ?? 0);
                            const transactions = Number(r.transactions ?? 0);
                            const quantity = Number(r.quantity ?? 0);
                            const amount = Number(r.amount ?? r.value ?? 0);
                            const ctr = views ? (clicks / views) * 100 : 0;
                            const cr = clicks ? (transactions / clicks) * 100 : 0;
                            return { date: r.date, views, clicks, ctr, transactions, cr, quantity, amount };
                        });
                        this.page = 1;
                    })
                    .catch(error => {
                        this.rows = [];
                        this.error = error;
                    });
            },
            async resetAndFetch() {
                this.sources = [];
                this.groups = [];
                this.rows = [];
                this.requestSummary.abortAll?.();
                this.requestSummary.resetAll?.();
            },
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
